/**
 * B2BStandalonePage — Module métier B2B Cliniques accessible directement
 * depuis le ModuleSelector (sans passer par l'onglet SADAdmin).
 *
 * Affiche le composant `B2BTab` dans une page autonome avec :
 *   - header propre (titre + retour ModuleSelector + bouton logout/changement module)
 *   - permissions auto-gérées par B2BTab (lecture seule / R/W selon rôle backend)
 *
 * Accès autorisé : admin, manager (Cyrille), supervisor (Bret), accountant (comptable).
 * Le contrôle d'accès est doublement enforced :
 *   - frontend : guard ici qui redirige les rôles non autorisés
 *   - backend  : 403 sur tous les endpoints `/api/b2b/*` si rôle non autorisé
 */
import React, { useEffect } from 'react';
import { Building2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import B2BTab from '../components/admin/B2B/B2BTab';

const AUTHORIZED_ROLES = new Set(['admin', 'manager', 'supervisor', 'accountant']);

const B2BStandalonePage = ({ onBack }) => {
  const { user } = useAuth();
  const role = user?.is_admin ? 'admin' : (user?.role || 'agent');
  const authorized = AUTHORIZED_ROLES.has(role);

  // Redirection si l'utilisateur n'a pas le rôle requis.
  useEffect(() => {
    if (!authorized) {
      onBack?.();
    }
  }, [authorized, onBack]);

  if (!authorized) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-white border-b border-zinc-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="b2b-page-back" title="Retour au sélecteur de modules">
              <ArrowLeft className="w-4 h-4 mr-1" /> Modules
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-zinc-900 truncate">SADA B2B</h1>
                <p className="text-[11px] sm:text-xs text-zinc-500 truncate">Demandes cliniques · cotation · exécution · paiement</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-block text-xs text-zinc-500">
              {user?.name} · <span className="font-semibold text-amber-700">{role}</span>
            </span>
            <Button variant="outline" size="sm" onClick={onBack} data-testid="b2b-page-change-module" title="Changer d'application métier sans se déconnecter">
              Changer de module
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-5">
        <B2BTab />
      </main>
    </div>
  );
};

export default B2BStandalonePage;
